import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { UserMinus, RotateCcw, AlertTriangle } from "lucide-react";

const norm = (a) => [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ");
const MOTIVOS_BAJA = [
  "Cambio de escuela",
  "Cambio de domicilio",
  "Deserción escolar",
  "Motivos de salud",
  "Motivos económicos",
  "Otro",
];

export default function Bajas() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState("");
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmar, setConfirmar] = useState(null); // { accion:'baja'|'reactivar', alumno }
  const [motivo, setMotivo] = useState(MOTIVOS_BAJA[0]);
  const [motivoLibre, setMotivoLibre] = useState("");
  const [doubleOk, setDoubleOk] = useState(false);
  const [saving, setSaving] = useState(false);

  const gradosDisponibles = useMemo(() => {
    const m = new Map();
    grupos.forEach((g) => m.set(g.grado, true));
    return [...m.keys()].sort((a, b) => a - b);
  }, [grupos]);

  const [grado, setGrado] = useState("");
  const gruposFiltrados = useMemo(() => grado ? grupos.filter((g) => String(g.grado) === String(grado)) : [], [grado, grupos]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length) setPlantelId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!plantelId) return;
    (async () => {
      try {
        const { data } = await api.get("/grupos", { params: { plantel_id: plantelId } });
        setGrupos(data);
        setGrado(""); setGrupoId(""); setAlumnos([]);
      } catch { /* ignore */ }
    })();
  }, [plantelId]);

  useEffect(() => {
    if (!grupoId) { setAlumnos([]); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/alumnos", { params: { grupo_id: grupoId, include_bajas: true } });
        setAlumnos(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [grupoId]);

  const activos = alumnos.filter((a) => a.activo !== false).sort((a, b) => norm(a).localeCompare(norm(b), "es"));
  const bajas = alumnos.filter((a) => a.activo === false).sort((a, b) => norm(a).localeCompare(norm(b), "es"));

  const abrirBaja = (a) => { setConfirmar({ accion: "baja", alumno: a }); setMotivo(MOTIVOS_BAJA[0]); setMotivoLibre(""); setDoubleOk(false); };
  const abrirReintegro = (a) => { setConfirmar({ accion: "reactivar", alumno: a }); setDoubleOk(false); };

  const ejecutar = async () => {
    if (!confirmar || !doubleOk) return;
    setSaving(true);
    try {
      const { alumno, accion } = confirmar;
      if (accion === "baja") {
        const motivoFinal = (motivo === "Otro" ? motivoLibre : motivo).trim();
        if (!motivoFinal) { toast.error("Indica el motivo de la baja"); setSaving(false); return; }
        await api.post(`/alumnos/${alumno.id}/baja`, { motivo: motivoFinal });
        setAlumnos((prev) => prev.map((a) => a.id === alumno.id ? { ...a, activo: false, motivo_baja: motivoFinal } : a));
        toast.success("Alumno dado de baja");
      } else {
        await api.post(`/alumnos/${alumno.id}/reactivar`);
        setAlumnos((prev) => prev.map((a) => a.id === alumno.id ? { ...a, activo: true, motivo_baja: "" } : a));
        toast.success("Alumno reintegrado a su grupo");
      }
      setConfirmar(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl">
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Matrícula</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Bajas de alumnos</h1>
          <p className="text-sm text-[#545454] mt-1">
            Cambie el estatus de un alumno a <strong>BAJA</strong> sin borrar sus datos. Este dato alimenta el formato 911 del siguiente ciclo. También puede reintegrar a un alumno dado de baja.
          </p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 1 && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Plantel</label>
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="baja-plantel"><SelectValue /></SelectTrigger>
                <SelectContent>{planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Grado</label>
            <Select value={grado} onValueChange={(v) => { setGrado(v); setGrupoId(""); }}>
              <SelectTrigger data-testid="baja-grado"><SelectValue placeholder="Selecciona grado…" /></SelectTrigger>
              <SelectContent>
                {gradosDisponibles.map((g) => <SelectItem key={g} value={String(g)}>{`${g}°`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Grupo</label>
            <Select value={grupoId} onValueChange={setGrupoId} disabled={!grado}>
              <SelectTrigger data-testid="baja-grupo"><SelectValue placeholder={grado ? "Selecciona grupo…" : "Elige grado primero"} /></SelectTrigger>
              <SelectContent>
                {gruposFiltrados.map((g) => <SelectItem key={g.id} value={g.id}>{`Grupo "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!grupoId ? (
          <div className="bg-white border border-dashed border-[#E5E7EB] rounded p-10 text-center text-sm text-[#9CA3AF]">
            Selecciona grado y grupo para ver los alumnos.
          </div>
        ) : loading ? (
          <div className="bg-white border border-[#E5E7EB] rounded p-10 text-center text-[#9CA3AF]">Cargando…</div>
        ) : (
          <>
            <Seccion titulo="Activos" contador={activos.length} tone="verde">
              {activos.length === 0 ? (
                <Empty msg="Sin alumnos activos en este grupo." />
              ) : (
                <ul className="divide-y divide-[#F3F4F6]">
                  {activos.map((a) => (
                    <li key={a.id} className="flex items-center justify-between py-2.5 px-3">
                      <div>
                        <div className="text-sm font-medium uppercase">{norm(a)}</div>
                        <div className="text-[11px] text-[#6B7280] font-mono">{a.curp || "—"}</div>
                      </div>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`btn-dar-baja-${a.id}`}
                        onClick={() => abrirBaja(a)}
                        className="h-7 text-xs border-[#9F2241] text-[#9F2241] hover:bg-[#9F2241] hover:text-white"
                      >
                        <UserMinus className="w-3 h-3 mr-1" /> Dar de baja
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Seccion>

            <Seccion titulo="Dados de baja" contador={bajas.length} tone="rojo">
              {bajas.length === 0 ? (
                <Empty msg="Sin bajas registradas en este grupo." />
              ) : (
                <ul className="divide-y divide-[#F3F4F6]">
                  {bajas.map((a) => (
                    <li key={a.id} className="flex items-center justify-between py-2.5 px-3 bg-[#FEF2F4]">
                      <div>
                        <div className="text-sm font-medium uppercase text-[#7C1E36]">{norm(a)}</div>
                        <div className="text-[11px] text-[#6B7280] font-mono">{a.curp || "—"}</div>
                        {a.motivo_baja && <div className="text-[11px] text-[#9F2241] mt-0.5">Motivo: {a.motivo_baja}</div>}
                      </div>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`btn-reintegrar-${a.id}`}
                        onClick={() => abrirReintegro(a)}
                        className="h-7 text-xs border-[#235B4E] text-[#235B4E] hover:bg-[#235B4E] hover:text-white"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Reintegrar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Seccion>
          </>
        )}
      </div>

      <Dialog open={!!confirmar} onOpenChange={(o) => { if (!o) { setConfirmar(null); setDoubleOk(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#9F2241]" />
              {confirmar?.accion === "baja" ? "Confirmar baja del alumno" : "Confirmar reintegración"}
            </DialogTitle>
            <DialogDescription>
              {confirmar?.accion === "baja"
                ? "El alumno se marcará con estatus BAJA. No se elimina; su información se conserva para el formato 911 del siguiente ciclo."
                : "El alumno volverá a estar activo dentro de su grupo. Esta acción también se registra en el histórico."}
            </DialogDescription>
          </DialogHeader>
          {confirmar && (
            <div className="space-y-3">
              <div className="p-3 bg-[#F9FAFB] rounded border border-[#F3F4F6]">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Alumno</div>
                <div className="text-sm font-semibold uppercase">{norm(confirmar.alumno)}</div>
                <div className="text-[11px] text-[#6B7280] mt-0.5 font-mono">{confirmar.alumno.curp}</div>
              </div>
              {confirmar.accion === "baja" && (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Motivo</label>
                    <Select value={motivo} onValueChange={setMotivo}>
                      <SelectTrigger data-testid="baja-motivo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MOTIVOS_BAJA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {motivo === "Otro" && (
                    <Textarea
                      value={motivoLibre}
                      onChange={(e) => setMotivoLibre(e.target.value)}
                      placeholder="Escribe el motivo…"
                      rows={2}
                      data-testid="baja-motivo-libre"
                    />
                  )}
                </>
              )}
              <label className="flex items-start gap-2 text-sm text-[#374151] cursor-pointer p-2 border border-[#E5E7EB] rounded">
                <input
                  type="checkbox"
                  checked={doubleOk}
                  onChange={(e) => setDoubleOk(e.target.checked)}
                  data-testid="baja-doublecheck"
                  className="mt-0.5"
                />
                <span>
                  Confirmo que deseo {confirmar.accion === "baja" ? "dar de baja" : "reintegrar"} a este alumno. Entiendo que esta acción cambia su estatus dentro del sistema.
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmar(null); setDoubleOk(false); }}>Cancelar</Button>
            <Button
              disabled={!doubleOk || saving}
              onClick={ejecutar}
              data-testid="btn-ejecutar-baja"
              className={confirmar?.accion === "baja"
                ? "bg-[#9F2241] hover:bg-[#7C1E36] text-white"
                : "bg-[#235B4E] hover:bg-[#1D4A40] text-white"}
            >
              {saving ? "Aplicando…" : (confirmar?.accion === "baja" ? "Sí, dar de baja" : "Sí, reintegrar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function Seccion({ titulo, contador, tone, children }) {
  const color = tone === "rojo" ? "#9F2241" : "#235B4E";
  return (
    <div className="mb-5 bg-white border border-[#E5E7EB] rounded">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F3F4F6]">
        <div className="text-xs tracking-widest uppercase" style={{ color }}>{titulo}</div>
        <div className="text-xs text-[#6B7280]">{contador} registro{contador === 1 ? "" : "s"}</div>
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="py-6 text-center text-sm text-[#9CA3AF]">{msg}</div>;
}
